<script setup>
import { computed } from 'vue';
import BaseIcon from '@/components/Base/BaseIcon.vue';
import BaseTooltip from '@/components/Base/BaseTooltip.vue';
import { opportunityLabelContext } from '@/composables/useOpportunityLabel';

const props = defineProps({
  opportunity: {
    type: Object,
    required: true,
  },
  segmentText: {
    type: String,
    required: false,
    default: null,
  },
  segmentSummary: {
    type: String,
    required: false,
    default: null,
  },
  size: {
    type: String,
    required: false,
    default: 'md',
    validator: (v) => ['sm', 'md'].includes(v),
  },
});

const context = computed(
  () => opportunityLabelContext[props.opportunity.value]
);

const wrapperClass = computed(() =>
  props.size === 'sm' ? 'w-[20px] h-[20px]' : 'w-[25px] h-[25px]'
);

const iconSize = computed(() => (props.size === 'sm' ? '12px' : '14px'));
</script>

<template>
  <div
    class="rounded-full flex items-center justify-center cursor-pointer shrink-0"
    :class="[wrapperClass, context?.bgClass]"
    data-testid="opportunity-label-badge"
  >
    <BaseIcon
      :name="context?.icon"
      :class="context?.iconClass"
      :size="iconSize"
    />
    <BaseTooltip
      tooltip-class="border border-proxify-gray-200 shadow-lg"
      :offset="[0, 6]"
    >
      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-1">
          <span
            class="text-[10px] font-semibold uppercase tracking-wide text-proxify-gray-500"
          >
            Segment
          </span>
          <span class="text-body-xs font-medium text-proxify-gray-700">
            {{ segmentText }}
          </span>
        </div>
        <div class="flex flex-row items-center gap-1">
          <div
            class="w-[18px] h-[18px] rounded-full flex flex-row items-center justify-center shrink-0"
          >
            <BaseIcon
              :name="context?.icon"
              :class="context?.iconClass"
              size="14px"
            />
          </div>
          <span
            class="text-body-xs font-semibold"
            :class="context?.textClass"
          >
            {{ opportunity.label }}
          </span>
        </div>
        <p
          v-if="segmentSummary"
          class="text-body-xs font-medium text-proxify-gray-600"
        >
          {{ segmentSummary }}
        </p>
      </div>
    </BaseTooltip>
  </div>
</template>
